"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "../utils";
import { Calendar } from "../calendar";
import { DateRangeInput } from "../date-range-input";
import { Icon } from "../icon";
function toDate(iso: string): Date {
  const parts = iso.split("-").map(Number);
  return new Date(parts[0] ?? 0, (parts[1] ?? 1) - 1, parts[2] ?? 1);
}
function toISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function formatDisplay(iso: string): string {
  const date = toDate(iso);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}
interface DateRangePickerProps {
  dateFrom: string | null;
  dateTo: string | null;
  onChange: (from: string | null, to: string | null) => void;
  startDateLabel?: string;
  endDateLabel?: string;
  className?: string;
}
const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>((t0, ref) => {
  const $ = _c(44);
  const {
    dateFrom,
    dateTo,
    onChange,
    startDateLabel: t1,
    endDateLabel: t2,
    className
  } = t0;
  const startDateLabel = t1 === undefined ? "Start Date" : t1;
  const endDateLabel = t2 === undefined ? "End Date" : t2;
  const [activeField, setActiveField] = React.useState("from");
  let t3;
  if ($[0] !== activeField || $[1] !== dateFrom || $[2] !== dateTo) {
    t3 = activeField === "from" && dateFrom ? toDate(dateFrom) : activeField === "to" && dateTo ? toDate(dateTo) : undefined;
    $[0] = activeField;
    $[1] = dateFrom;
    $[2] = dateTo;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selectedDate = t3;
  let t4;
  if ($[4] !== dateFrom) {
    t4 = dateFrom ? toDate(dateFrom) : new Date();
    $[4] = dateFrom;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const defaultMonth = t4;
  let t5;
  if ($[6] !== activeField || $[7] !== dateFrom || $[8] !== dateTo || $[9] !== onChange) {
    t5 = date => {
      if (!date) {
        return;
      }
      const dateStr = toISO(date);
      if (activeField === "from") {
        const newTo = dateTo && dateStr > dateTo ? null : dateTo;
        onChange(dateStr, newTo);
        setActiveField("to");
      } else {
        onChange(dateFrom, dateStr);
      }
    };
    $[6] = activeField;
    $[7] = dateFrom;
    $[8] = dateTo;
    $[9] = onChange;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleCalendarSelect = t5;
  let t6;
  if ($[11] !== className) {
    t6 = cn("flex flex-col gap-6", className);
    $[11] = className;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== dateFrom) {
    t7 = dateFrom ? formatDisplay(dateFrom) : null;
    $[13] = dateFrom;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = activeField === "from";
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setActiveField("from");
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== startDateLabel || $[17] !== t7 || $[18] !== t8) {
    t10 = <DateRangeInput label={startDateLabel} displayValue={t7} active={t8} onClick={t9} />;
    $[16] = startDateLabel;
    $[17] = t7;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Icon name="arrow-right" className="size-5 shrink-0 text-text-secondary" />;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== dateTo) {
    t12 = dateTo ? formatDisplay(dateTo) : null;
    $[21] = dateTo;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = activeField === "to";
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setActiveField("to");
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== endDateLabel || $[25] !== t12 || $[26] !== t13) {
    t15 = <DateRangeInput label={endDateLabel} displayValue={t12} active={t13} onClick={t14} />;
    $[24] = endDateLabel;
    $[25] = t12;
    $[26] = t13;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t10 || $[29] !== t15) {
    t16 = <div className="flex items-center gap-2">{t10}{t11}{t15}</div>;
    $[28] = t10;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== activeField || $[32] !== dateFrom) {
    t17 = activeField === "to" && dateFrom ? {
      before: toDate(dateFrom)
    } : undefined;
    $[31] = activeField;
    $[32] = dateFrom;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== defaultMonth || $[35] !== handleCalendarSelect || $[36] !== selectedDate || $[37] !== t17) {
    t18 = <Calendar mode="single" selected={selectedDate} onSelect={handleCalendarSelect} defaultMonth={defaultMonth} disabled={t17} />;
    $[34] = defaultMonth;
    $[35] = handleCalendarSelect;
    $[36] = selectedDate;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== ref || $[40] !== t16 || $[41] !== t18 || $[42] !== t6) {
    t19 = <div ref={ref} data-slot="date-range-picker" className={t6}>{t16}{t18}</div>;
    $[39] = ref;
    $[40] = t16;
    $[41] = t18;
    $[42] = t6;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  return t19;
});
DateRangePicker.displayName = "DateRangePicker";
export { DateRangePicker };
export type { DateRangePickerProps };